import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';

import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import NpoGeneralForm from '@/components/forms/NpoGeneralForm';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { GeneralData } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { FileToUpload, uploadMultipleToFireStorage } from '@/utils/uploadMultipleToFireStorage';

const NpoGeneral = () => {
  const { npoJson } = useLocalSearchParams<{ npoJson: string }>();
  const { state: authState } = useAuth();
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();
  const { showError, showSuccess } = useEndFlowModal();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoJson ?? '');

  const updateNonProfit = ({
    name,
    description,
    email,
    address,
    location,
    totalEmployees,
    totalVolunteers,
    showMap,
    thumbnail
  }: {
    name: string;
    description: string | undefined;
    email: string | undefined;
    address: string;
    location: string;
    totalEmployees: number;
    totalVolunteers: number;
    showMap: boolean | undefined;
    thumbnail: string[] | null;
  }) => {
    nonProfitUpdate(
      {
        npoId: authState.signUpData?.loginEmail?.npoId ?? '',
        name: name,
        description: description,
        email: email,
        address: address,
        location: location,
        totalEmployees: totalEmployees,
        totalVolunteers: totalVolunteers,
        showMap: showMap ?? undefined,
        thumbnail: thumbnail
      },
      {
        onSuccess: (_) => {
          showSuccess({
            title: i18n.t('end_flow.success_title'),
            message: i18n.t('npo_details.update_message')
          });
          router.back();
        },
        onError: (e) => {
          setIsLoading(false);
          showError({ message: e.message });
          console.error('Error', e);
        }
      }
    );
  };

  const onSubmit = async (data: GeneralData) => {
    try {
      setIsLoading(true);
      let filesToUpload: FileToUpload[] = [];
      if (data.thumbnail) {
        data.thumbnail.map((thumb, index) => {
          if (thumb) {
            filesToUpload.push({
              uri: thumb,
              name: `thumb_${index}.${new Date().toISOString()}`
            });
          }
        });
      }

      if (filesToUpload.length > 0) {
        await uploadMultipleToFireStorage(
          `non-profits-orgs/${authState.signUpData?.loginEmail?.npoId}/thumbs/`,
          filesToUpload,
          (_, progress) => {
            setFileProgress(progress);
          }
        ).then((storagedFiles) => {
          const mappedFiles = storagedFiles.map((result) => result.metadata.fullPath);
          updateNonProfit({
            name: data.name,
            description: data.description,
            email: data.email,
            address: data.address,
            location: data.location,
            totalEmployees: data.totalEmployees,
            totalVolunteers: data.totalVolunteers,
            showMap: data.showMap ?? undefined,
            thumbnail: mappedFiles
          });
        });
      } else {
        updateNonProfit({
          name: data.name,
          description: data.description,
          email: data.email,
          address: data.address,
          location: data.location,
          totalEmployees: data.totalEmployees,
          totalVolunteers: data.totalVolunteers,
          showMap: data.showMap ?? undefined,
          thumbnail: null
        });
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      console.error(e);
    }
  };

  return (
    <NpoGeneralForm
      nonProfitOrg={nonProfitOrg}
      isLoading={isLoading || nonProfitIsPending}
      fileProgress={fileProgress}
      showBackButton={false}
      submitLabel={i18n.t('mix.done')}
      onSubmitTapped={onSubmit}
    />
  );
};

export default NpoGeneral;
